/**
 * Вход и регистрация: логин и пароль либо внешний провайдер (Google, GitHub, Яндекс, Microsoft,
 * Discord — те, что настроены на сервере). Администратор входит в ту же форму своим логином.
 * Также обрабатывает возврат от провайдера: /auth/complete#token=… | #linked=… | #error=….
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { cr } from '../../lib/format';
import { navigate } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { ProviderIcon } from './ProviderIcon';

type Mode = 'login' | 'register';

export function LoginPage() {
  const info = useApp((s) => s.info);
  const authError = useApp((s) => s.authError);
  const [mode, setMode] = useState<Mode>('login');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providers = info?.auth.providers ?? [];

  useEffect(() => setError(null), [mode]);
  // сообщение сервера о завершённой сессии («сессия не найдена», блокировка) показываем один раз
  useEffect(() => {
    if (authError && !/сессия не найдена/.test(authError)) setError(authError);
  }, [authError]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'register' && password !== password2) return setError('пароли не совпадают');
    setBusy(true);
    try {
      const r = mode === 'login' ? await api.login(login.trim(), password) : await api.register(login.trim(), password, name.trim());
      client.signIn(r.token);
      navigate(r.role === 'admin' ? '/admin' : '/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const sso = async (id: string) => {
    setBusy(true);
    try {
      location.assign((await api.oauthStart(id)).url);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const valid = mode === 'login' ? login.trim() && password : login.trim().length >= 3 && password.length >= 8 && name.trim().length >= 2 && password2;

  return (
    <div className="auth-page">
      <section className="auth-intro">
        <h1>Рыночный стол</h1>
        <p className="lead">Прогноз движения скрытого индекса из трёх акций S&amp;P 500. Сначала — только график, затем шкала, и лишь в последнем круге — состав.</p>
        <ul className="auth-points">
          <li>
            <b>4–8 игроков</b> за столом, три круга ставок по минуте
          </li>
          <li>
            <b>Проверяемый расчёт:</b> подписанный пакет с котировками и проводками
          </li>
          <li>
            <b>История и статистика</b> всех ваших розыгрышей в профиле
          </li>
        </ul>
        <p className="demo-note">Демо: при регистрации начисляется {info ? cr(info.startingBalance) : '10 000 кр.'} игровых кредитов. Реальных денег нет.</p>
      </section>

      <form className="panel auth-card" onSubmit={submit} noValidate>
        <div className="segmented auth-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>
            Вход
          </button>
          <button type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'on' : ''} onClick={() => setMode('register')}>
            Регистрация
          </button>
        </div>

        <label className="field">
          Логин
          <input autoFocus autoComplete="username" value={login} maxLength={32} onChange={(e) => setLogin(e.target.value)} placeholder="латиница, цифры, . _ -" />
        </label>
        {mode === 'register' && (
          <label className="field">
            Имя за столом
            <input autoComplete="nickname" value={name} maxLength={24} onChange={(e) => setName(e.target.value)} placeholder="например, Ирина" />
          </label>
        )}
        <label className="field">
          Пароль
          <input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} maxLength={128} onChange={(e) => setPassword(e.target.value)} />
          {mode === 'register' && <small className="field-hint">не короче 8 символов, буквы и цифры</small>}
        </label>
        {mode === 'register' && (
          <label className="field">
            Пароль ещё раз
            <input type="password" autoComplete="new-password" value={password2} maxLength={128} onChange={(e) => setPassword2(e.target.value)} />
          </label>
        )}
        {error && (
          <p className="notice danger" role="alert">
            {error}
          </p>
        )}
        <button className="btn primary wide" disabled={busy || !valid}>
          {mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
        </button>

        {providers.length > 0 && (
          <>
            <div className="auth-sep">
              <span>или через</span>
            </div>
            <div className="sso-grid">
              {providers.map((p) => (
                <button key={p.id} type="button" className="btn sso" disabled={busy} onClick={() => void sso(p.id)}>
                  <ProviderIcon id={p.id} />
                  {p.title}
                </button>
              ))}
            </div>
          </>
        )}
      </form>
    </div>
  );
}

/** Возврат от провайдера (/auth/complete#…): вход, привязка или ошибка. */
export function useAuthComplete(): void {
  useEffect(() => {
    if (location.pathname !== '/auth/complete') return;
    const h = new URLSearchParams(location.hash.slice(1));
    history.replaceState(null, '', '/auth/complete');
    const token = h.get('token');
    const linked = h.get('linked');
    const error = h.get('error');
    if (token) {
      client.signIn(token);
      if (h.get('created')) client.toast('info', 'Учётная запись создана. Имя за столом можно изменить в профиле.');
      navigate('/', { replace: true });
    } else if (linked) {
      client.toast('info', `${linked}: учётная запись привязана к профилю`);
      navigate('/profile', { replace: true });
    } else {
      client.toast('error', error ?? 'вход не выполнен');
      navigate(client.state.profile ? '/profile' : '/', { replace: true });
    }
  }, []);
}
